import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type MarkingQueue, type SessionRow } from '../../api';

const waitingFor = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
};

const nameOf = (r: { firstName: string; lastName: string | null }) =>
  `${r.firstName}${r.lastName ? ` ${r.lastName}` : ''}`;

/**
 * Everything waiting for a person to read. The oldest is first, because that is
 * the trainee who has been waiting longest to find out whether they passed.
 */
export function Marking() {
  const [queue, setQueue] = useState<MarkingQueue | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [q, s] = await Promise.all([
        api.get<MarkingQueue>('/instructor/marking'),
        api.get<{ sessions: SessionRow[] }>('/instructor/sessions'),
      ]);
      setQueue(q);
      setSessions(s.sessions);
    } catch (e) { setError((e as ApiError).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (error) return <p className="auth__error">{error}</p>;
  if (!queue) return <p className="muted">Loading…</p>;

  const total = queue.counts.tests + queue.counts.assignments;
  const registers = sessions.filter((s) => Number(s.unmarked) > 0 && new Date(s.startsAt) < new Date());

  return (
    <>
      <div className="page-head">
        <h1 className="h1">To mark</h1>
        <span className="muted">{queue.scope === 'all' ? 'every course' : 'the courses you teach'}</span>
      </div>

      {total === 0 && registers.length === 0 && (
        <p className="notice">Nothing is waiting. Written answers and assignments appear here as they come in.</p>
      )}

      {queue.counts.tests > 0 && (
        <section>
          <h2 className="h2">Written answers ({queue.counts.tests})</h2>
          <table className="grid">
            <thead>
              <tr><th>Trainee</th><th>Test</th><th>Course</th><th>Waiting</th><th /></tr>
            </thead>
            <tbody>
              {queue.tests.map((t) => (
                <tr key={t.attemptId}>
                  <td>{nameOf(t)}</td>
                  <td>{t.unitName}{t.attemptNo > 1 && <span className="muted small"> attempt {t.attemptNo}</span>}</td>
                  <td className="muted">{t.courseName}</td>
                  <td>{waitingFor(t.submittedAt)}</td>
                  <td><Link className="btn btn--primary btn--small" to={`/marking/tests/${t.attemptId}`}>Mark</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {queue.counts.assignments > 0 && (
        <section>
          <h2 className="h2">Assignments ({queue.counts.assignments})</h2>
          <table className="grid">
            <thead>
              <tr><th>Trainee</th><th>Assignment</th><th>Course</th><th>Attachment</th><th>Waiting</th><th /></tr>
            </thead>
            <tbody>
              {queue.assignments.map((a) => (
                <tr key={a.submissionId}>
                  <td>{nameOf(a)}</td>
                  <td>{a.unitName}</td>
                  <td className="muted">{a.courseName}</td>
                  <td>{a.hasFile ? 'yes' : <span className="muted">text only</span>}</td>
                  <td>{waitingFor(a.submittedAt)}</td>
                  <td><Link className="btn btn--primary btn--small" to={`/marking/assignments/${a.submissionId}`}>Mark</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {registers.length > 0 && (
        <section>
          <h2 className="h2">Registers not taken ({registers.length})</h2>
          <table className="grid">
            <thead>
              <tr><th>Session</th><th>Course</th><th>When</th><th>Signed up</th><th>Unmarked</th><th /></tr>
            </thead>
            <tbody>
              {registers.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td className="muted">{s.courseName}</td>
                  <td>{new Date(s.startsAt).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="num">{s.registered}</td>
                  <td className="num">{s.unmarked}</td>
                  <td><Link className="btn btn--primary btn--small" to={`/marking/sessions/${s.id}`}>Take register</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {sessions.length > 0 && (
        <section>
          <h2 className="h2">All live sessions</h2>
          <table className="grid">
            <thead>
              <tr><th>Session</th><th>Course</th><th>When</th><th>Signed up</th><th /></tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td className="muted">{s.courseName}</td>
                  <td>{new Date(s.startsAt).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="num">{s.registered}</td>
                  <td><Link to={`/marking/sessions/${s.id}`}>open</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
